// Service Wizard: what a service typically includes, against the family's budget for it —
// a caterer's menus and how many dishes each has, decoration packages with pictures, price
// ranges — and the vendors near the function place (within a radius by default; further
// away on request, ordered by the family's priorities). Anonymous: no vendor names or
// contacts until the family's contract is signed.
import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Typography, Stack, Chip, Grid, Paper, CircularProgress, Tooltip, Button, TextField, MenuItem, Alert, Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaletteIcon from '@mui/icons-material/Palette';
import PlaceIcon from '@mui/icons-material/Place';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import { SERVICE_SHOWCASE_QUERY, NEARBY_VENDORS_QUERY } from '../graphql/queries';
import { RadiusChips, DEFAULT_RADIUS, distanceLabel } from './LocationFilter';
import PhotoStrip from './PhotoStrip';
import { mediaUrl } from '../utils/media';

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
// Prices charged per guest: the budget buys a number of plates/guests rather than one package
const PER_GUEST = /\bper (plate|person|guest|head)\b/i;
const perWord = (unit) => (unit || '').match(PER_GUEST)?.[1] || 'guest';

// Fallback artwork for decoration packages without a photo
const GRADIENTS = [
  'linear-gradient(135deg, #7b1fa2 0%, #e91e63 100%)',
  'linear-gradient(135deg, #f9a825 0%, #e65100 100%)',
  'linear-gradient(135deg, #00897b 0%, #7cb342 100%)',
  'linear-gradient(135deg, #3949ab 0%, #8e24aa 100%)',
];

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended (within budget, best match, nearest)' },
  { value: 'budget', label: 'Within my budget first' },
  { value: 'distance', label: 'Nearest first' },
  { value: 'match', label: 'Best match to my answers' },
  { value: 'price', label: 'Price: low to high' },
];

function Stat({ value, label }) {
  return (
    <Box sx={{ textAlign: 'center', px: 1.5, minWidth: 90 }}>
      <Typography variant="h6" fontWeight={700} color="primary.main" lineHeight={1.2}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

// With a guest count, per-plate prices become the cost for the whole function
function BudgetFit({ pkg, budget, guests }) {
  if (!(budget > 0) || !(pkg.price > 0)) return null;
  if (PER_GUEST.test(pkg.priceUnit || '') && guests > 0) {
    const cost = pkg.price * guests;
    return cost <= budget
      ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`${inr(cost)} for ${guests} — within budget`} />
      : <Chip size="small" color="warning" variant="outlined" label={`${inr(cost)} for ${guests} — over budget`} />;
  }
  if (PER_GUEST.test(pkg.priceUnit || '')) {
    return <Chip size="small" color="info" variant="outlined" label={`≈ ${Math.floor(budget / pkg.price).toLocaleString('en-IN')} ${perWord(pkg.priceUnit)}s on your budget`} />;
  }
  return pkg.price <= budget
    ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Within your budget" />
    : <Chip size="small" color="warning" variant="outlined" label={`${inr(pkg.price - budget)} over your budget`} />;
}

// artwork: a background for the card's picture band (decoration), or null for none
// onChoose: makes this the preferred package (Service Wizard); chosen: it is the preferred one
function PackageCard({ pkg, budget, guests, itemLabel, artwork, onChoose, chosen }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', borderColor: chosen ? 'primary.main' : undefined, borderWidth: chosen ? 2 : 1 }}>
      {/* The vendor's photo of the package (first item photo), else decoration artwork */}
      {pkg.photos?.length > 0 ? (
        <Box
          role="img"
          aria-label={pkg.photos[0].caption || pkg.name}
          sx={{ height: 120, background: `center / cover no-repeat url("${mediaUrl(pkg.photos[0].url)}")`, bgcolor: '#f5f5f5' }}
        />
      ) : artwork && (
        <Box sx={{ height: 90, background: artwork, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', p: 1 }}>
          <PaletteIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 30 }} />
          {pkg.description && (
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, textAlign: 'right', ml: 1 }}>{pkg.description}</Typography>
          )}
        </Box>
      )}
      <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700}>{pkg.name}</Typography>
            <Typography variant="caption" color="text.secondary">{inr(pkg.price)} {pkg.priceUnit}</Typography>
          </Box>
          {pkg.itemCount > 0 && (
            <Box sx={{ textAlign: 'center', bgcolor: '#f3e5f5', borderRadius: 2, px: 1, py: 0.5, flexShrink: 0 }}>
              <Typography variant="h6" fontWeight={800} color="primary.main" lineHeight={1}>{pkg.itemCount}</Typography>
              <Typography variant="caption" color="primary.dark" sx={{ fontSize: 10 }}>{itemLabel}</Typography>
            </Box>
          )}
        </Stack>

        {pkg.sections.length > 0 && (
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
            {pkg.sections.map(s => (
              <Tooltip key={s.name} title={s.items.join(', ') + (s.count > s.items.length ? ` and ${s.count - s.items.length} more` : '')}>
                <Chip size="small" variant="outlined" label={`${s.name} · ${s.count}`} />
              </Tooltip>
            ))}
          </Stack>
        )}
        {pkg.photos?.length > 0 && <PhotoStrip photos={pkg.photos} size={40} max={5} />}
        {pkg.sections.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {pkg.sections.flatMap(s => s.items).slice(0, 8).join(' · ')}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap mt="auto">
          <BudgetFit pkg={pkg} budget={budget} guests={guests} />
          {chosen && <Chip size="small" color="primary" icon={<StarIcon />} label="Preferred" />}
          {!chosen && onChoose && pkg.id && <Button size="small" onClick={() => onChoose(pkg)}>Choose</Button>}
        </Stack>
      </Box>
    </Paper>
  );
}

function VendorFitChip({ v }) {
  if (v.budgetFit === 'per_guest') {
    return <Chip size="small" color="info" label={`≈ ${v.guestsOnBudget.toLocaleString('en-IN')} ${perWord(v.priceUnit)}s on your budget`} />;
  }
  if (v.budgetFit === 'within') return <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Within your budget" />;
  if (v.budgetFit === 'over') return <Chip size="small" color="warning" variant="outlined" label="Above your budget" />;
  return null;
}

function VendorCard({ v, place, budget, guests, itemLabel, isDecoration, preferred, onChoose }) {
  const distance = distanceLabel(v, place);
  const isPreferred = preferred?.offeringId === v.offeringId;
  const choose = onChoose && ((pkg) => onChoose({
    offeringId: v.offeringId, title: v.title, city: v.city, distanceKm: v.distanceKm,
    packageId: pkg?.id || null, packageName: pkg?.name || null,
    price: pkg?.price ?? v.cheapestPrice, priceUnit: pkg?.priceUnit || v.priceUnit,
    reasons: ['Chosen by you'],
  }));
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: isPreferred ? 'primary.main' : undefined, borderWidth: isPreferred ? 2 : 1 }}>
      {isPreferred && (
        <Chip size="small" color="primary" icon={<StarIcon />} sx={{ mb: 1 }}
          label={preferred.auto ? 'Preferred vendor — picked automatically' : 'Preferred vendor — chosen by you'} />
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={1}>
        <Box minWidth={0}>
          <Typography variant="subtitle1" fontWeight={700}>{v.title}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <PlaceIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{v.city}</Typography>
            {distance && <Chip size="small" variant="outlined" label={distance} />}
            {v.servesEventCity && v.distanceKm > 0 && <Chip size="small" color="secondary" variant="outlined" label={`Serves ${place}`} />}
          </Stack>
        </Box>
        <Stack alignItems={{ sm: 'flex-end' }} spacing={0.5} flexShrink={0}>
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {inr(v.priceFrom)}{v.priceTo && v.priceTo !== v.priceFrom ? `–${inr(v.priceTo)}` : '+'} {v.priceUnit}
          </Typography>
          <VendorFitChip v={v} />
          {choose && !isPreferred && (
            <Button size="small" variant="outlined" onClick={() => choose(v.packages[0] || null)}>Choose this vendor</Button>
          )}
        </Stack>
      </Stack>

      {(v.matches.length > 0 || v.highlights.length > 0) && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1}>
          {v.matches.map(m => <Chip key={`m-${m}`} size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label={m} />)}
          {v.highlights.filter(h => !v.matches.includes(h)).map(h => <Chip key={h} size="small" label={h} sx={{ bgcolor: '#fff8e1' }} />)}
        </Stack>
      )}

      {v.packages.length > 0 && (
        <Grid container spacing={1}>
          {v.packages.map((pkg, i) => (
            <Grid item xs={12} sm={6} md={4} key={`${pkg.name}-${i}`}>
              <PackageCard
                pkg={pkg} budget={budget} guests={guests} itemLabel={itemLabel}
                artwork={isDecoration ? GRADIENTS[i % GRADIENTS.length] : null}
                onChoose={choose || undefined}
                chosen={isPreferred && preferred.packageId === pkg.id}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}

function NearbyVendors({ category, displayName, eventCity, budget, guestCount, preferences, keywords, itemLabel, isDecoration, preferred, onChoose }) {
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS);
  const [sort, setSort] = useState('recommended');
  const [showOutside, setShowOutside] = useState(false);
  const place = (eventCity || '').trim();
  const { data, loading } = useQuery(NEARBY_VENDORS_QUERY, {
    variables: { category, eventCity: place || null, radiusKm, budget: budget || null, guestCount: guestCount || null, preferences, keywords, sort },
    fetchPolicy: 'cache-and-network',
  });
  const r = data?.nearbyVendors;
  const cardProps = { place: r?.eventCity || place, budget, guests: guestCount, itemLabel, isDecoration, preferred, onChoose };

  if (!r) return loading ? <Stack alignItems="center" py={2}><CircularProgress size={24} /></Stack> : null;

  // Without a known function place there is no radius: everyone, in priority order
  const noRadius = !place || !r.cityKnown;
  const nearby = noRadius ? [...r.within, ...r.outside] : r.within;

  return (
    <Box mt={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} mb={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          {noRadius ? `${displayName} vendors` : `${displayName} vendors within ${r.radiusKm} km of ${r.eventCity}`}
        </Typography>
        {!noRadius && <RadiusChips value={radiusKm} onChange={(km) => { setRadiusKm(km); setShowOutside(false); }} />}
      </Stack>

      {!place && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Add where the function will take place (Wedding Budget step) to see the vendors near it first.
        </Alert>
      )}
      {place && !r.cityKnown && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          We don't have the location of "{place}" yet, so vendors are listed by your priorities without distances.
        </Alert>
      )}

      {r.listLocked ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {r.withinCount + r.outsideCount === 0
            ? `No ${displayName.toLowerCase()} vendors are listed on the platform yet. Your matchmaker will share options.`
            : noRadius
              ? `${r.withinCount + r.outsideCount} ${displayName.toLowerCase()} vendor${r.withinCount + r.outsideCount === 1 ? '' : 's'} match your choices.`
              : `${r.withinCount} ${displayName.toLowerCase()} vendor${r.withinCount === 1 ? '' : 's'} within ${r.radiusKm} km of ${r.eventCity}${r.outsideCount ? ` and ${r.outsideCount} further away` : ''}.`}
          {' '}The list of vendors opens once your contract for this service is signed. Your matchmaker will pick a suitable vendor for you meanwhile.
        </Alert>
      ) : (<>
      <TextField select size="small" label="Order by" value={sort} onChange={e => setSort(e.target.value)} sx={{ mb: 1.5, minWidth: 280 }}>
        {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>

      {nearby.length === 0 && (
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {!r.within.length && !r.outside.length
            ? `No ${displayName.toLowerCase()} vendors are listed on the platform yet. Your matchmaker will share options.`
            : noRadius
              ? `No ${displayName.toLowerCase()} vendors to show.`
              : `No ${displayName.toLowerCase()} vendors within ${r.radiusKm} km of ${r.eventCity}. Choose a wider distance above, or see the ones further away below.`}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {nearby.map(v => <VendorCard key={v.offeringId} v={v} {...cardProps} />)}
      </Stack>

      {!noRadius && r.outside.length > 0 && (
        <Box mt={1.5}>
          <Button
            size="small"
            variant="outlined"
            endIcon={showOutside ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowOutside(s => !s)}
          >
            {showOutside ? 'Hide' : 'Show'} {r.outside.length} vendor{r.outside.length === 1 ? '' : 's'} beyond {r.radiusKm} km
          </Button>
          <Collapse in={showOutside} unmountOnExit>
            <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={1}>
              Ordered by: {SORT_OPTIONS.find(o => o.value === sort)?.label}
            </Typography>
            <Stack spacing={1.5}>
              {r.outside.map(v => <VendorCard key={v.offeringId} v={v} {...cardProps} />)}
            </Stack>
          </Collapse>
        </Box>
      )}
      </>)}
    </Box>
  );
}

// The same search as the vendor list (default radius, recommended order), reporting only its
// automatic pick — so every selected service gets a preferred vendor without being opened
export function AutoVendorPicker({ category, eventCity, budget, guestCount, preferences = [], keywords = [], onPick }) {
  const { data } = useQuery(NEARBY_VENDORS_QUERY, {
    variables: {
      category, eventCity: (eventCity || '').trim() || null, radiusKm: DEFAULT_RADIUS, budget: budget || null,
      guestCount: guestCount || null, preferences, keywords, sort: 'recommended',
    },
    fetchPolicy: 'cache-and-network',
  });
  const pick = data?.nearbyVendors ? data.nearbyVendors.pick || null : undefined;
  const key = pick === undefined ? undefined : `${pick?.offeringId || ''}:${pick?.packageId || ''}`;
  // Reports only when the pick itself changes (onPick is a new function every render)
  useEffect(() => { if (pick !== undefined) onPick(pick); }, [key]);
  return null;
}

// preferred / onChoose: the service's preferred vendor and how to change it (Service Wizard)
export default function ServiceShowcase({ category, displayName, budget, eventCity, guestCount, preferences = [], keywords = [], preferred, onChoose }) {
  const { data, loading, error } = useQuery(SERVICE_SHOWCASE_QUERY, { variables: { category }, fetchPolicy: 'cache-first' });
  const s = data?.serviceShowcase;

  if (loading && !s) return <Stack alignItems="center" py={2}><CircularProgress size={24} /></Stack>;
  if (error || !s) return null;
  const isDecoration = category === 'decoration';
  const vendorSearch = (
    <NearbyVendors
      category={category}
      displayName={displayName}
      eventCity={eventCity}
      budget={budget}
      guestCount={guestCount}
      preferences={preferences}
      keywords={keywords}
      preferred={preferred}
      onChoose={onChoose}
      itemLabel={s.itemLabel}
      isDecoration={isDecoration}
    />
  );
  // Nothing to show about the service yet: still the vendor search (radius, order by, beyond the radius)
  if (!s.packageCount && !s.images.length && !s.themes.length && !s.providerCount) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No {displayName} packages to show yet. Your matchmaker will share options once the wishlist is approved.
        </Typography>
        {vendorSearch}
      </Box>
    );
  }

  const perGuest = PER_GUEST.test(s.priceUnit || '');

  return (
    <Box>
      {/* In numbers */}
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="center" divider={<Box sx={{ borderLeft: '1px solid #e0e0e0' }} />} sx={{ mb: 2 }}>
        <Stat value={s.packageCount} label={s.packageLabel} />
        {s.priceFrom != null && (
          <Stat
            value={s.priceTo && s.priceTo !== s.priceFrom ? `${inr(s.priceFrom)}–${inr(s.priceTo)}` : inr(s.priceFrom)}
            label={s.priceUnit || 'price'}
          />
        )}
        {s.packages.length > 0 && (
          <Stat
            value={`${Math.min(...s.packages.map(p => p.itemCount))}–${Math.max(...s.packages.map(p => p.itemCount))}`}
            label={`${s.itemLabel} per ${s.packageLabel.replace(/s$/, '').toLowerCase()}`}
          />
        )}
        {perGuest && budget > 0 && s.priceFrom > 0 && (
          <Stat value={`≈ ${Math.floor(budget / s.priceFrom).toLocaleString('en-IN')}`} label={`${perWord(s.priceUnit)}s at the lowest price`} />
        )}
      </Stack>

      {s.highlights.length > 0 && (
        <Stack spacing={0.75} mb={2}>
          {s.highlights.map(h => (
            <Stack key={h.label} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ minWidth: 110 }}>{h.label}</Typography>
              {h.values.map(v => <Chip key={v} size="small" label={v} sx={{ bgcolor: '#fff8e1' }} />)}
            </Stack>
          ))}
        </Stack>
      )}

      {/* Photo gallery: vendors' photos of their items, and service / decoration-theme pictures
          added by the admin. Click to view full size. */}
      {s.images.length > 0 && (
        <Box mb={2}>
          <PhotoStrip photos={s.images} size={110} max={8} />
        </Box>
      )}

      {isDecoration && s.themes.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
          {s.themes.map(t => (
            <Paper key={t.name} variant="outlined" sx={{ p: 1, borderRadius: 2, minWidth: 140 }}>
              <Stack direction="row" spacing={0.5} mb={0.5}>
                {(t.colorPalette.length ? t.colorPalette : ['#7b1fa2', '#f9a825']).slice(0, 5).map(c => (
                  <Box key={c} sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: c, border: '1px solid rgba(0,0,0,0.1)' }} />
                ))}
              </Stack>
              <Typography variant="caption" fontWeight={600} display="block">{t.name}</Typography>
              {t.estimatedCost != null && <Typography variant="caption" color="text.secondary">{inr(t.estimatedCost)}</Typography>}
            </Paper>
          ))}
        </Stack>
      )}

      {vendorSearch}
    </Box>
  );
}
